import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Vault, Users as UsersIcon, KeyRound } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", description: "", color: "#FF8A00" };

export default function GroupsPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try { const { data } = await api.get("/groups"); setItems(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (g) => { setEditing(g); setForm({ name: g.name, description: g.description || "", color: g.color || "#FF8A00" }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Nome obrigatório");
    try {
      if (editing) {
        await api.put(`/groups/${editing.id}`, form);
        toast.success("Cofre atualizado");
      } else {
        await api.post("/groups", form);
        toast.success("Cofre criado");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao salvar"));
    }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/groups/${toDelete.id}`);
      toast.success("Cofre removido");
      setToDelete(null);
      fetchAll();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao excluir"));
    }
  };

  return (
    <div className="space-y-6" data-testid="groups-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: "#FF8A00" }}>Controle de acesso</div>
          <h2 className="font-display font-bold text-3xl mt-1 text-[#1F1F24]">Cofres</h2>
          <p className="text-gray-600 text-sm mt-1">
            Agrupe credenciais por setor. Membros vinculados ao cofre visualizam todas as credenciais dele (sem permissão para editar — defina permissões individuais nas credenciais para liberar edição).
          </p>
        </div>
        {isAdmin && (
          <Button className="vp-btn-primary text-white h-11 px-5 rounded-lg" onClick={openNew} data-testid="btn-new-group">
            <Plus className="w-4 h-4 mr-2" /> Novo cofre
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? <div className="text-gray-500">Carregando...</div> : items.map((g) => (
          <div key={g.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`group-card-${g.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${g.color}20`, color: g.color }}>
                  <Vault className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-[#1F1F24] truncate" data-testid={`group-name-${g.id}`}>{g.name}</div>
                  <div className="text-xs text-gray-500 truncate">{g.description || "Sem descrição"}</div>
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => openEdit(g)} className="p-1.5 text-gray-500 hover:text-[#FF8A00] hover:bg-orange-50 rounded-md" data-testid={`group-edit-${g.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => setToDelete(g)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md" data-testid={`group-delete-${g.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-600">
              <span className="inline-flex items-center gap-1"><UsersIcon className="w-3.5 h-3.5" /> {g.member_count} membro(s)</span>
              <span className="inline-flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> {g.credential_count} credencial(is)</span>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cofre" : "Novo cofre"}</DialogTitle>
            <DialogDescription className="text-sm text-gray-500">Agrupe credenciais e usuários por setor da empresa.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="g-name">Nome</Label>
              <Input id="g-name" data-testid="group-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
            </div>
            <div>
              <Label htmlFor="g-desc">Descrição</Label>
              <Textarea id="g-desc" data-testid="group-input-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div>
              <Label htmlFor="g-color">Cor</Label>
              <Input id="g-color" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-11 w-24 p-1" data-testid="group-input-color" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="vp-btn-primary text-white" data-testid="group-form-submit">{editing ? "Atualizar" : "Criar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cofre</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja excluir <strong>{toDelete?.name}</strong>? Usuários vinculados serão desvinculados.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
